<template>
  <div
    v-if="hasImages || (blok && imageSource.src)"
    class="h-full"
    :class="{ 'cursor-pointer': !!blok?.fields?.url }"
    @click="handleNavigation"
  >
    <!-- Use CloudinaryImg component for optimized loading -->
    <template v-if="hasImages">
      <!-- Mobile image (hidden on desktop) -->
      <CloudinaryImg
        v-if="mobileImage?.secure_url"
        :src="mobileImage.secure_url"
        :alt="mobileImage.public_id"
        :loading="preload ? 'eager' : 'lazy'"
        :height="`${mobileImage?.height}`"
        :sizes="sizes"
        :quality="preload ? 85 : 60"
        class="size-full object-cover md:hidden"
        :class="{ 'mx-auto max-w-screen-2xl': isTeaser }"
      />

      <CloudinaryImg
        v-if="primaryImage"
        :src="primaryImage.secure_url"
        :alt="primaryImage.public_id"
        :height="`${primaryImage?.height}`"
        :sizes="sizes"
        :quality="preload ? 85 : 60"
        class="size-full object-cover"
        :loading="preload ? 'eager' : 'lazy'"
        :class="[
          {
            'mx-auto max-w-screen-2xl': isTeaser,
            'max-md:hidden md:block': primaryImage.secure_url,
          },
        ]"
      />
    </template>

    <!-- Fallback to contentful image if no cloudinary images -->
    <NuxtPicture
      v-else-if="blok && imageSource.src"
      v-element-visibility="onVisible"
      provider="contentful"
      class="picture h-full bg-gray-200"
      :class="isCover ? 'picture-cover' : 'picture-contain'"
      :sizes="sizes"
      :src="imageSource.src"
      :preload="preload"
      :fetchpriority="preload ? 'high' : 'auto'"
      :alt="imageSource.alt"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { vElementVisibility } from '@vueuse/components'
import type {
  ChainModifiers,
  Entry,
  EntryFieldTypes,
  EntrySkeletonType,
  LocaleCode,
} from 'contentful'
import {
  getTeaserImage,
  useContentfulImageSanitizer,
} from '../composables/useContentfulImage'
import type {
  TypeImageFields,
  TypePageWithoutUnresolvableLinksResponse,
} from '../types'
import type { CloudinaryImage } from '../types/types'
import { NuxtPicture } from '#components'
import CloudinaryImg from '~/components/ui/CloudinaryImg.vue'
import { navigateTo } from '#app/composables/router'
import { isExternalLink } from '~/utils'

export interface TypeImageFieldsWithTypes extends TypeImageFields {
  cloudinaryDesktopImage?: EntryFieldTypes.Object<CloudinaryImage[]>
  cloudinaryMobileImage?: EntryFieldTypes.Object<CloudinaryImage[]>
}

export type TypeImageSkeletonWithTypes = EntrySkeletonType<
  TypeImageFieldsWithTypes,
  'image'
>
export type TypeImageWithTypes<
  Modifiers extends ChainModifiers,
  Locales extends LocaleCode = LocaleCode,
> = Entry<TypeImageSkeletonWithTypes, Modifiers, Locales>

export type TypeImageWithoutUnresolvableLinksResponse = TypeImageWithTypes<
  'WITHOUT_UNRESOLVABLE_LINKS',
  LocaleCode
>

export type CMSImageProps = {
  blok: TypeImageWithoutUnresolvableLinksResponse
  // eslint-disable-next-line vue/prop-name-casing
  _uid?: string
  sizes?: string
  preload?: boolean
  isTeaser?: boolean
  isCover?: boolean
}

const {
  blok,
  preload,
  isCover = false,
  isTeaser = false,
  sizes = 'xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw 2xl:100vw',
} = defineProps<CMSImageProps>()

const { sanitize } = useContentfulImageSanitizer()
const hasBeenVisible = ref(false)
if (!blok) {
  throw new Error('blok is required')
}

const desktopImage = computed(
  () => blok?.fields.cloudinaryDesktopImage?.[0] as CloudinaryImage,
)

const mobileImage = computed(
  () => blok?.fields.cloudinaryMobileImage?.[0] as CloudinaryImage,
)

const hasImages = computed(() => desktopImage.value || mobileImage.value)

// Use desktop image as default for SSR, mobile will be handled by CSS
const primaryImage = computed(() => desktopImage.value || mobileImage.value)

const handleNavigation = async () => {
  if (!blok?.fields?.url) {
    return
  }

  await navigateTo(blok?.fields?.url, {
    external: isExternalLink(blok?.fields.url),
  })
}

const imageSource = computed(() =>
  isTeaser
    ? getTeaserImage(
        blok as unknown as TypePageWithoutUnresolvableLinksResponse,
      )
    : sanitize(blok as unknown as TypeImageWithoutUnresolvableLinksResponse),
)

const onVisible = (state: boolean) => {
  const promotionId = blok?.fields?.tracking?.fields?.promotion_id ?? ''

  if (!promotionId || !state || hasBeenVisible.value) {
    return
  }

  hasBeenVisible.value = true
}

defineOptions({ name: 'CMSImage' })
</script>
